import {
  BufferGeometry,
  Camera,
  CylinderGeometry,
  Material,
  Mesh,
  MeshBasicMaterial,
  OrthographicCamera,
  Scene,
  Vector3,
  WebGLRenderer,
} from "three";
import { Quick3DPerspectiveCamera } from "../camera/quick3d-perspective-camera";
import * as Config from "../config/ViewerConfig";
import { AutoAttenuableState } from "../measurement/types";

export class AutoAttenuableLine extends Mesh {
  private _points: Vector3[];
  private _correctPoints: Vector3[];
  private _offsets: Vector3[];
  private _defaultGeometry: BufferGeometry;
  private _selectedGeometry: BufferGeometry;
  private _hoveredGeometry: BufferGeometry;
  private _defaultMaterial: Material;
  private _selectedMaterial: Material;
  private _hoveredMaterial: Material;

  get points() {
    return this._points;
  }
  constructor(
    length: number,
    material: Material = new MeshBasicMaterial({ color: 0x0000ff })
  ) {
    const geometry = new CylinderGeometry(
      Config.DEFAULT_LINE_RADIUS,
      Config.DEFAULT_LINE_RADIUS,
      length,
      8,
      1,
      true
    );
    geometry.lookAt(new Vector3(0, 1, 0));
    super(geometry, material);
    this._defaultGeometry = geometry;
    const selectedGeometry = new CylinderGeometry(
      Config.SELECTED_LINE_RADIUS,
      Config.SELECTED_LINE_RADIUS,
      length,
      8,
      1,
      true
    );
    selectedGeometry.lookAt(new Vector3(0, 1, 0));
    this._selectedGeometry = selectedGeometry;

    const hoveredGeometry = new CylinderGeometry(
      Config.HOVERED_LINE_RADIUS,
      Config.HOVERED_LINE_RADIUS,
      length,
      8,
      1,
      true
    );

    hoveredGeometry.lookAt(new Vector3(0, 1, 0));
    this._hoveredGeometry = hoveredGeometry;

    this._defaultMaterial = material;
    this._selectedMaterial = new MeshBasicMaterial({
      color: Config.SELECTED_MEASUREMENT_COLOR,
      depthTest: false,
      transparent: true,
    });
    this._hoveredMaterial = new MeshBasicMaterial({
      color: Config.HOVERED_MEASUREMENT_COLOR,
      depthTest: false,
      transparent: true,
    });
    this._points = [new Vector3(), new Vector3()];
    this._correctPoints = [new Vector3(), new Vector3()];
    this._offsets = [new Vector3(), new Vector3()];

    this.onBeforeRender = function (
      _ifcRenderer: WebGLRenderer,
      _nonClippingScene: Scene,
      camera: Camera
    ): void {
      let scale = 1;
      const windowScale = Math.sqrt(
        (1280 * 720) / (window.screen.width * window.screen.height)
      );
      if (camera instanceof OrthographicCamera) {
        scale =
          ((camera.right - camera.left) / camera.zoom) * 0.01 * windowScale;
      }
      if (camera instanceof Quick3DPerspectiveCamera) {
        const minDistance = Math.min(
          this.position.distanceTo(camera.position),
          this._points[0]?.distanceTo(camera.position),
          this._points[1]?.distanceTo(camera.position)
        );
        scale = minDistance * 0.02 * windowScale;
      }

      this.scale.set(scale, scale, 1);
      this.updateMatrixWorld();
    };
  }

  public updateLength(length: number) {
    this.geometry = new CylinderGeometry(
      Config.DEFAULT_LINE_RADIUS,
      Config.DEFAULT_LINE_RADIUS,
      length,
      8,
      1,
      true
    );
    this.geometry.lookAt(new Vector3(0, 1, 0));

    this._defaultGeometry = this.geometry;

    this._selectedGeometry = new CylinderGeometry(
      Config.SELECTED_LINE_RADIUS,
      Config.SELECTED_LINE_RADIUS,
      length,
      8,
      1,
      true
    );

    this._selectedGeometry.lookAt(new Vector3(0, 1, 0));

    this._hoveredGeometry = new CylinderGeometry(
      Config.HOVERED_LINE_RADIUS,
      Config.HOVERED_LINE_RADIUS,
      length,
      8,
      1,
      true
    );
    this._hoveredGeometry.lookAt(new Vector3(0, 1, 0));
  }

  public updatePoints(point1: Vector3, point2: Vector3) {
    const center = new Vector3().addVectors(point1, point2).multiplyScalar(0.5);
    let length = point1.distanceTo(point2);

    this.updateLength(length);
    this.position.copy(center);
    this.lookAt(point2);
    this.updateMatrixWorld();
    this._points = [point1, point2];
  }

  public dispose(): void {
    if (this.parent) {
      this.parent.remove(this);
    }
    this.geometry?.dispose();
    if (Array.isArray(this.material)) {
      for (const m of this.material) {
        m.dispose();
      }
    } else {
      this.material?.dispose();
    }
    this.geometry = undefined as unknown as BufferGeometry;
    this.material = undefined as unknown as Material;
  }

  public setPoints(point1: Vector3, point2: Vector3) {
    this._correctPoints = [point1, point2];
  }

  public setOffset(offset1: Vector3, offset2: Vector3) {
    this._offsets = [offset1, offset2];
  }

  public setMaterialByState(state: AutoAttenuableState) {
    this.material = {
      default: this._defaultMaterial,
      hovered: this._hoveredMaterial,
      selected: this._selectedMaterial,
    }[state];
  }

  public setGeometryByState(state: AutoAttenuableState) {
    this.geometry = {
      default: this._defaultGeometry,
      hovered: this._hoveredGeometry,
      selected: this._selectedGeometry,
    }[state];
  }

  public changeRenderOrderByState(state: AutoAttenuableState) {
    this.renderOrder = {
      default: 1,
      hovered: 1,
      selected: 1,
    }[state];
  }
}
